import * as React from 'react';

/**
 * MAN belt logo — renders one of the two brand assets at /public/brand/:
 *   - variant="blue":  belt-icon-blue.png  (512×512 raster, brand-primary baked in)
 *   - variant="white": belt-icon-white.svg (384×384 SVG; the motif is a raster image
 *                                            embedded inside the SVG, so it can't be
 *                                            recoloured via CSS — it's already white)
 *
 * Both variants render as <img>. Tailwind `text-*` utilities have no effect on the
 * image fill — the colour is baked into each asset.
 */
interface BeltIconProps {
    className?: string;
    variant: 'blue' | 'white';
    'aria-hidden'?: boolean;
    alt?: string;
    /**
     * React 19 emits a high-priority `<link rel="preload" as="image">` for every <img>
     * rendered during SSR — verified in the built output (dist/blog/index.html carries
     * one for belt-icon-blue.png). These assets are heavy (belt-icon-white.svg is 162KB,
     * 112KB gzipped: a raster embedded in an SVG wrapper), so a BeltIcon rendered in
     * page CHROME would preload that weight on every prerendered page and contend with
     * the LCP hero photo's own preload. Passing `loading="lazy"` makes React skip the
     * preload; pair it with `fetchPriority="low"`. Required for below-the-fold uses.
     */
    loading?: 'eager' | 'lazy';
    fetchPriority?: 'high' | 'low' | 'auto';
}

const VARIANT_SRC: Record<BeltIconProps['variant'], string> = {
    blue: '/brand/belt-icon-blue.png',
    white: '/brand/belt-icon-white.svg',
};

const BeltIcon: React.FC<BeltIconProps> = ({
    className,
    variant,
    'aria-hidden': ariaHidden = true,
    alt = '',
    loading,
    fetchPriority,
}) => (
    <img
        src={VARIANT_SRC[variant]}
        alt={alt}
        aria-hidden={ariaHidden}
        className={className}
        loading={loading}
        fetchPriority={fetchPriority}
    />
);

export default BeltIcon;
